import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useConvex, useMutation, useQuery } from "convex/react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  ArrowLeft,
  Check,
  Copy,
  Download,
  History,
  Printer,
  RefreshCw,
  Search,
} from "lucide-react";
import { formatMinute } from "@convex/lib/time";
import AudioPlayer, { type AudioPlayerHandle } from "@/components/AudioPlayer";
import TranscriptView from "@/components/TranscriptView";

/**
 * react-markdown plus highlight.js is roughly half the bundle, and the phone
 * that records a lecture never opens this page mid-lesson. Splitting it keeps
 * the recorder's first load small.
 */
const MarkdownView = lazy(() => import("@/components/MarkdownView"));
import {
  copyText,
  downloadText,
  exportFilename,
  printDocument,
  transcriptMarkdown,
} from "@/lib/export";
import { statusLabel } from "@/lib/status";
import {
  cardClass,
  errorMessage,
  inputClass,
  monoMetaClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "@/lib/ui";

/**
 * Lesson detail - the read side of the whole pipeline (PLAN.md §11, Phase 8).
 *
 * A lesson can hold more than one recording (stopped and restarted mid-lecture),
 * so the page is a recording switcher over two panes: the notes and the timed
 * transcript. The player is pinned above both because the transcript's
 * timestamps drive it.
 */
export default function Lesson() {
  const params = useParams<{ lessonId: string }>();
  const lessonId = params.lessonId as Id<"lessons"> | undefined;

  const lesson = useQuery(
    api.lessons.get,
    lessonId === undefined ? "skip" : { id: lessonId },
  );
  const parts = useQuery(
    api.summaries.byLesson,
    lessonId === undefined ? "skip" : { lessonId },
  );

  const [selected, setSelected] = useState(0);
  const [hasChangedRecording, setHasChangedRecording] = useState(false);
  const index =
    parts === undefined ? 0 : Math.min(selected, Math.max(parts.length - 1, 0));
  const part = parts === undefined || parts.length === 0 ? null : parts[index];

  if (lessonId === undefined || lesson === null) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="mx-auto max-w-3xl"
      >
        <BackLink />
        <p className={`mt-6 ${monoMetaClass}`}>That lesson no longer exists.</p>
      </motion.div>
    );
  }

  return (
    <div className="print-sheet mx-auto max-w-3xl">
      <BackLink date={lesson?.date} />

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="relative pb-4"
      >
        <h1 className="mt-3 text-2xl font-light tracking-[-0.05em] text-[#222] sm:text-3xl dark:text-[#ededeb]">
          {lesson === undefined ? (
            <span className="inline-block h-8 w-56 rounded-md bg-neutral-200/60 dark:bg-neutral-800/60 animate-pulse" />
          ) : (
            (lesson.title ?? lesson.subjectName)
          )}
        </h1>
        {lesson === undefined ? (
          <div className="mt-2 flex items-center gap-2">
            <span className="inline-block h-3.5 w-44 rounded bg-neutral-200/50 dark:bg-neutral-800/50 animate-pulse" />
            <span className="inline-block h-3.5 w-24 rounded bg-neutral-200/50 dark:bg-neutral-800/50 animate-pulse" />
          </div>
        ) : (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            className={`mt-1 ${monoMetaClass}`}
          >
            {new Date(`${lesson.date}T00:00:00`).toLocaleDateString(undefined, {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
            {" · "}
            {formatMinute(lesson.startMinute)}–{formatMinute(lesson.endMinute)}
            {lesson.location !== undefined && ` · ${lesson.location}`}
            {lesson.category !== undefined && ` · ${lesson.category}`}
            {" · "}
            {statusLabel(lesson.status)}
          </motion.p>
        )}
        {lesson !== undefined && lesson.notes !== undefined && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15, delay: 0.05 }}
            className="mt-2 text-sm text-neutral-600 dark:text-neutral-400"
          >
            {lesson.notes}
          </motion.p>
        )}
        <div className="meniscus pointer-events-none absolute bottom-0 left-0 h-[1px] w-full opacity-40" />
      </motion.div>

      {parts === undefined && (
        <div className={`mt-6 ${cardClass} animate-pulse space-y-4`}>
          <div className="flex items-center gap-3">
            <div className="h-3.5 w-16 rounded bg-neutral-200/60 dark:bg-neutral-800/60" />
            <div className="h-3.5 w-20 rounded bg-neutral-200/60 dark:bg-neutral-800/60" />
            <div className="h-3.5 w-24 rounded bg-neutral-200/60 dark:bg-neutral-800/60" />
          </div>
          <div className="h-14 rounded-xl bg-neutral-100 dark:bg-neutral-900/60" />
          <div className="h-28 rounded-xl bg-neutral-100/50 dark:bg-neutral-900/40" />
        </div>
      )}

      {parts !== undefined && parts.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className={`mt-6 ${cardClass}`}
        >
          <p className={monoMetaClass}>
            No recording is attached to this lesson yet. Start one from the calendar,
            or attach an existing capture from Unassigned.
          </p>
        </motion.div>
      )}

      {parts !== undefined && parts.length > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="no-print mt-6 flex flex-wrap items-center gap-1.5"
        >
          <span className="font-mono text-[10px] tracking-wider text-neutral-500 uppercase">
            Recording
          </span>
          {parts.map((candidate, position) => (
            <motion.button
              key={candidate.recording._id}
              type="button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                setHasChangedRecording(true);
                setSelected(position);
              }}
              className={[
                "relative cursor-pointer rounded-lg px-2.5 py-1 font-mono text-[11px] transition-colors duration-150",
                position === index
                  ? "font-semibold text-[#222] dark:text-[#ededeb]"
                  : "text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100",
              ].join(" ")}
            >
              {position === index && (
                hasChangedRecording ? (
                  <motion.span
                    layoutId="lesson-part-pill"
                    className="absolute inset-0 rounded-lg bg-neutral-900/[0.08] dark:bg-white/[0.12]"
                    transition={{ type: "spring", stiffness: 450, damping: 35 }}
                  />
                ) : (
                  <span className="absolute inset-0 rounded-lg bg-neutral-900/[0.08] dark:bg-white/[0.12]" />
                )
              )}
              <span className="relative z-10">
                {position + 1} ·{" "}
                {new Date(candidate.recording.startedAt).toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </motion.button>
          ))}
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {part !== null && lesson !== undefined && (
          <RecordingPanel
            key={part.recording._id}
            part={part}
            subjectName={lesson.title ?? lesson.subjectName}
            date={lesson.date}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function BackLink({ date }: { date?: string }) {
  const location = useLocation();
  const locationState = location.state as { fromDate?: string; fromView?: string } | null;
  const returnDate =
    locationState?.fromDate ||
    (() => {
      try {
        return sessionStorage.getItem("lls_calendar_cursor");
      } catch {
        return null;
      }
    })() ||
    date;

  const returnView =
    locationState?.fromView ||
    (() => {
      try {
        return sessionStorage.getItem("lls_calendar_view");
      } catch {
        return null;
      }
    })();

  const params = new URLSearchParams();
  if (returnDate) params.set("date", returnDate);
  if (returnView && returnView !== "week") params.set("view", returnView);
  const qs = params.toString();
  const to = qs ? `/?${qs}` : "/";

  return (
    <Link
      to={to}
      className="no-print group inline-flex items-center gap-1.5 font-mono text-[11px] text-neutral-500 transition-colors hover:text-neutral-900 dark:hover:text-neutral-100"
    >
      <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-150 group-hover:-translate-x-0.5" />
      Calendar
    </Link>
  );
}

type LessonParts = NonNullable<
  ReturnType<typeof useQuery<typeof api.summaries.byLesson>>
>;
type Part = LessonParts[number];

function RecordingPanel({
  part,
  subjectName,
  date,
}: {
  part: Part;
  subjectName: string;
  date: string;
}) {
  const recordingId = part.recording._id;
  const convex = useConvex();
  const regenerate = useMutation(api.summaries.regenerate);
  const forgetAudio = useMutation(api.retention.forgetAudio);
  const removeTranscript = useMutation(api.transcripts.remove);
  const versions = useQuery(api.summaries.versions, { recordingId });

  const [tab, setTab] = useState<"summary" | "transcript">("summary");
  const [hasChangedTab, setHasChangedTab] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [filter, setFilter] = useState("");
  const [currentMs, setCurrentMs] = useState<number | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which half the open confirm dialog is for. The two deletes are separate on
  // purpose (PLAN.md 9): the audio is what fills the disk, the transcript is
  // what the lecture was for.
  const [deleteTarget, setDeleteTarget] = useState<"audio" | "transcript" | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const player = useRef<AudioPlayerHandle | null>(null);

  // A regeneration lands as a new latest version, and the pick is keyed on the
  // one it was made against - so a new summary drops the reader back onto it
  // instead of stranding them on the version they were comparing.
  const latestId = part.summary?._id;
  const [pick, setPick] = useState<{
    latest: Id<"summaries"> | undefined;
    id: Id<"summaries"> | null;
  }>({ latest: latestId, id: null });
  const versionId = pick.latest === latestId ? pick.id : null;

  // The picked version is a separate document; the latest one already rode
  // along on the roll-up, so showing it until something else is picked is free.
  const picked = useQuery(
    api.summaries.get,
    versionId === null ? "skip" : { id: versionId },
  );
  const summary = versionId === null ? part.summary : (picked ?? null);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1_500);
    return () => clearTimeout(timer);
  }, [copied]);

  const filename = useMemo(() => exportFilename(subjectName, date), [subjectName, date]);
  const transcriptFilename = filename.replace(/\.md$/, "-transcript.md");

  /**
   * The viewer pages segments as it scrolls, so an export cannot reuse what is
   * on screen - it walks every page itself.
   */
  async function loadTranscript(): Promise<string> {
    const segments: { index: number; startMs: number; text: string }[] = [];
    for (;;) {
      const page = await convex.query(api.transcripts.segments, {
        recordingId,
        fromIndex: segments.length === 0 ? 0 : segments[segments.length - 1].index + 1,
        limit: 1_000,
      });
      segments.push(...page);
      if (page.length < 1_000) break;
    }
    return transcriptMarkdown(`${subjectName} — ${date} (transcript)`, segments);
  }

  async function handleTranscriptExport(kind: "copy" | "download") {
    setBusy(true);
    setError(null);
    try {
      const markdown = await loadTranscript();
      if (kind === "copy") setCopied(await copyText(markdown));
      else downloadText(transcriptFilename, markdown);
    } catch (caught) {
      setError(errorMessage(caught, "Could not export the transcript."));
    } finally {
      setBusy(false);
    }
  }

  const job = part.summaryJob;
  const running = job !== null && (job.state === "pending" || job.state === "leased");

  async function handleRegenerate() {
    setBusy(true);
    setError(null);
    try {
      await regenerate({ recordingId });
    } catch (caught) {
      setError(errorMessage(caught, "Could not queue the summary."));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Deletes one half of this recording. Audio: freed ahead of its retention
   * date (PLAN.md 9), with the worker unlinking the file on its next sweep.
   * Transcript: the text and every summary made from it.
   *
   * Deleting the second half leaves an empty header, so the server clears the
   * record itself - the panel then goes with the lesson's roll-up.
   */
  async function handleConfirmDelete() {
    if (deleteTarget === null) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      if (deleteTarget === "audio") {
        await forgetAudio({ recordingId });
      } else {
        await removeTranscript({ recordingId });
      }
      setDeleteTarget(null);
    } catch (caught) {
      const what = deleteTarget === "audio" ? "audio" : "transcript";
      setDeleteError(errorMessage(caught, `Could not delete the ${what}.`));
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      className={`print-sheet mt-6 ${cardClass}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className={monoMetaClass}>
          {new Date(part.recording.startedAt).toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
        {part.recording.durationMs !== undefined && (
          <span className={monoMetaClass}>
            {Math.max(1, Math.round(part.recording.durationMs / 60_000))} min
          </span>
        )}
        {part.recording.fileName !== undefined && (
          <span
            className={`${monoMetaClass} max-w-[220px] truncate`}
            title={part.recording.fileName}
          >
            {part.recording.fileName}
          </span>
        )}
        <span className={monoMetaClass}>{part.recording.state}</span>
        {part.transcript !== null && (
          <span className={monoMetaClass}>
            {part.transcript.characters.toLocaleString()} chars · {part.transcript.model}
          </span>
        )}
        {part.recording.gapSeqs.length > 0 && (
          <span className="font-mono text-xs text-[#F59E0B]">
            {part.recording.gapSeqs.length} missing chunk
            {part.recording.gapSeqs.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {part.recording.error !== undefined && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
          {part.recording.error}
        </p>
      )}

      <div className="no-print mt-4">
        <AudioPlayer
          ref={player}
          recordingId={recordingId}
          durationMs={part.recording.durationMs}
          hasAudio={part.recording.audioPath !== undefined}
          audioDeletedAt={part.recording.audioDeletedAt}
          onTimeChange={setCurrentMs}
        />
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          {part.recording.audioPath !== undefined && (
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={busy || deleteBusy}
              onClick={() => {
                setDeleteError(null);
                setDeleteTarget("audio");
              }}
              className={deleteLinkClass}
            >
              Delete audio now
            </motion.button>
          )}
          {part.transcript !== null && (
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={busy || deleteBusy}
              onClick={() => {
                setDeleteError(null);
                setDeleteTarget("transcript");
              }}
              className={deleteLinkClass}
            >
              Delete transcript now
            </motion.button>
          )}
        </div>
      </div>

      <div className="no-print mt-5 flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex items-center gap-1.5">
          {(["summary", "transcript"] as const).map((name) => (
            <motion.button
              key={name}
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setHasChangedTab(true);
                setTab(name);
              }}
              className={[
                "relative cursor-pointer rounded-xl px-3 py-1.5 text-xs font-medium capitalize transition-colors duration-150",
                tab === name
                  ? "font-semibold text-[#222] dark:text-white"
                  : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100",
              ].join(" ")}
            >
              {tab === name && (
                hasChangedTab ? (
                  <motion.span
                    layoutId="lesson-tab-pill"
                    className="absolute inset-0 rounded-xl bg-neutral-900/[0.08] dark:bg-white/[0.12]"
                    transition={{ type: "spring", stiffness: 450, damping: 35 }}
                  />
                ) : (
                  <span className="absolute inset-0 rounded-xl bg-neutral-900/[0.08] dark:bg-white/[0.12]" />
                )
              )}
              <span className="relative z-10">{name}</span>
            </motion.button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
          {tab === "summary" && summary !== null && (
            <>
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.96 }}
                className={secondaryButtonClass}
                onClick={() => {
                  void copyText(summary.markdown).then(setCopied);
                }}
              >
                <span className="flex items-center gap-1.5 min-w-[52px] justify-center">
                  <AnimatePresence mode="wait" initial={false}>
                    {copied ? (
                      <motion.span
                        key="copied"
                        initial={{ scale: 0.7, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.7, opacity: 0 }}
                        transition={{ duration: 0.12 }}
                        className="flex items-center gap-1.5 text-[#22C55E]"
                      >
                        <Check className="h-3.5 w-3.5" />
                        Copied
                      </motion.span>
                    ) : (
                      <motion.span
                        key="copy"
                        initial={{ scale: 0.7, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.7, opacity: 0 }}
                        transition={{ duration: 0.12 }}
                        className="flex items-center gap-1.5"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Copy
                      </motion.span>
                    )}
                  </AnimatePresence>
                </span>
              </motion.button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.96 }}
                className={secondaryButtonClass}
                onClick={() => downloadText(filename, summary.markdown)}
              >
                <span className="flex items-center gap-1.5">
                  <Download className="h-3.5 w-3.5" />
                  .md
                </span>
              </motion.button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.96 }}
                className={secondaryButtonClass}
                onClick={() => printDocument(filename.replace(/\.md$/, ""))}
              >
                <span className="flex items-center gap-1.5">
                  <Printer className="h-3.5 w-3.5" />
                  PDF
                </span>
              </motion.button>
              {versions !== undefined && versions.length > 1 && (
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.96 }}
                  className={secondaryButtonClass}
                  onClick={() => setShowHistory((open) => !open)}
                >
                  <span className="flex items-center gap-1.5">
                    <History className="h-3.5 w-3.5" />v{summary.version}
                  </span>
                </motion.button>
              )}
            </>
          )}
          {tab === "transcript" && part.transcript !== null && (
            <>
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.96 }}
                className={secondaryButtonClass}
                disabled={busy}
                onClick={() => void handleTranscriptExport("copy")}
              >
                <span className="flex items-center gap-1.5 min-w-[52px] justify-center">
                  {copied ? (
                    <span className="flex items-center gap-1.5 text-[#22C55E]">
                      <Check className="h-3.5 w-3.5" />
                      Copied
                    </span>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </>
                  )}
                </span>
              </motion.button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.96 }}
                className={secondaryButtonClass}
                disabled={busy}
                onClick={() => void handleTranscriptExport("download")}
              >
                <span className="flex items-center gap-1.5">
                  <Download className="h-3.5 w-3.5" />
                  .md
                </span>
              </motion.button>
            </>
          )}
          {tab === "summary" && part.transcript !== null && (
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
              className={primaryButtonClass}
              disabled={busy || running}
              onClick={() => void handleRegenerate()}
            >
              <span className="flex items-center gap-1.5">
                <RefreshCw
                  className={`h-3.5 w-3.5 ${busy || running ? "animate-spin" : ""}`}
                />
                {running ? "Running…" : summary === null ? "Summarize" : "Regenerate"}
              </span>
            </motion.button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {error !== null && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 text-xs text-red-600 dark:text-red-400 overflow-hidden"
          >
            {error}
          </motion.p>
        )}
        {job !== null && job.state === "failed" && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 text-xs text-red-600 dark:text-red-400 overflow-hidden"
          >
            Summary failed after {job.attempts} attempt{job.attempts === 1 ? "" : "s"}
            {job.error === undefined ? "." : `: ${job.error}`}
          </motion.p>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showHistory && versions !== undefined && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -4 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -4 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="no-print mt-3 flex flex-col gap-1 overflow-hidden"
          >
            {versions.map((version) => (
              <motion.button
                key={version._id}
                type="button"
                whileHover={{ x: 2 }}
                onClick={() => {
                  setPick({
                    latest: latestId,
                    id: version._id === latestId ? null : version._id,
                  });
                  setShowHistory(false);
                }}
                className={[
                  "flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1 text-left font-mono text-[11px] transition-colors",
                  summary?._id === version._id
                    ? "bg-neutral-900/[0.06] text-[#222] dark:bg-white/[0.08] dark:text-[#ededeb]"
                    : "text-neutral-500 hover:bg-[#fafaf8] dark:hover:bg-[#1a1a19]",
                ].join(" ")}
              >
                <span className="w-8 shrink-0">v{version.version}</span>
                <span className="shrink-0">
                  {new Date(version.createdAt).toLocaleString(undefined, {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span className="truncate">{version.model}</span>
                <span className="ml-auto shrink-0">
                  prompt v{version.promptVersion} · ${version.costUsd.toFixed(4)}
                </span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-5">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
          >
            {tab === "summary" ? (
              summary === null ? (
                <p className={monoMetaClass}>
                  {part.transcript === null
                    ? "No transcript yet - the summary runs once transcription finishes."
                    : running
                      ? "Summary queued. It appears here when the worker finishes."
                      : "No summary yet. Press Summarize."}
                </p>
              ) : (
                <>
                  <Suspense
                    fallback={<p className={monoMetaClass}>Rendering…</p>}
                  >
                    <MarkdownView markdown={summary.markdown} />
                  </Suspense>
                  <p className={`no-print mt-6 ${monoMetaClass}`}>
                    v{summary.version} · {summary.model} · prompt v{summary.promptVersion} ·{" "}
                    {summary.tokensIn.toLocaleString()} in /{" "}
                    {summary.tokensOut.toLocaleString()} out · $
                    {summary.costUsd.toFixed(4)}
                  </p>
                </>
              )
            ) : (
              <>
                <div className="no-print relative mb-3 flex items-center">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 shrink-0 text-neutral-400" />
                  <input
                    type="search"
                    value={filter}
                    placeholder="Filter the loaded segments"
                    onChange={(event) => setFilter(event.target.value)}
                    className={`${inputClass} w-full py-1.5 !pl-8 text-xs`}
                  />
                </div>
                <TranscriptView
                  recordingId={recordingId}
                  currentMs={currentMs}
                  filter={filter}
                  onSeek={(ms) => player.current?.seekTo(ms)}
                />
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {deleteTarget !== null && (
          <DeletePartDialog
            target={deleteTarget}
            // The other half is already gone, so this delete empties the record
            // rather than trimming it. Worth saying before it happens.
            clearsRecord={
              deleteTarget === "audio"
                ? part.transcript === null
                : part.recording.audioPath === undefined
            }
            busy={deleteBusy}
            error={deleteError}
            onConfirm={() => void handleConfirmDelete()}
            onClose={() => {
              if (!deleteBusy) setDeleteTarget(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const deleteLinkClass =
  "cursor-pointer text-[11px] text-neutral-500 underline-offset-2 transition-colors hover:text-red-600 hover:underline disabled:opacity-40 dark:text-neutral-400 dark:hover:text-red-400";

function DeletePartDialog({
  target,
  clearsRecord,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  target: "audio" | "transcript";
  clearsRecord: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const isAudio = target === "audio";
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) {
        onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [busy, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => {
        if (!busy) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-xs"
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-audio-dialog-title"
        initial={{ scale: 0.95, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 10 }}
        transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-[#d1d1cf] bg-[#fdfdfc] p-6 shadow-xl dark:border-[#2a2a28] dark:bg-[#141413]"
      >
        <div className="meniscus absolute top-0 left-0 h-[1px] w-full opacity-60" />
        <div className="flex items-center justify-between border-b border-[#d1d1cf]/60 pb-3 dark:border-[#2a2a28]">
          <span className="font-mono text-xs uppercase tracking-wider text-neutral-500">
            {isAudio ? "Delete audio" : "Delete transcript"}
          </span>
        </div>

        <div className="mt-4">
          <h3
            id="delete-audio-dialog-title"
            className="text-base font-semibold tracking-tight text-[#222] dark:text-[#ededeb]"
          >
            {isAudio
              ? "Delete this recording's audio?"
              : "Delete this recording's transcript?"}
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-neutral-600 dark:text-neutral-400">
            {isAudio
              ? "The transcript and notes are kept, but the recording can no longer be played or re-transcribed."
              : "The transcript and every summary made from it go. The audio is kept, so the lecture can be transcribed again."}
          </p>
          {clearsRecord && (
            <p className="mt-2 text-xs leading-relaxed text-red-600 dark:text-red-400">
              Nothing else is left of this recording, so the record itself is removed
              from the lesson.
            </p>
          )}
        </div>

        {error !== null && (
          <p className="mt-3 text-xs text-red-600 dark:text-red-400">{error}</p>
        )}

        <div className="mt-6 flex items-center justify-end gap-2.5">
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            disabled={busy}
            onClick={onClose}
            className={secondaryButtonClass}
          >
            Cancel
          </motion.button>
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            disabled={busy}
            onClick={onConfirm}
            className="cursor-pointer rounded-xl bg-red-600 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-white shadow-xs transition-all hover:bg-red-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 dark:bg-red-600 dark:hover:bg-red-500"
          >
            {busy ? "Deleting…" : isAudio ? "Delete audio" : "Delete transcript"}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
